import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@spark';
import './versionsDiff.css';

/**
 * Line-clamps arbitrary JSX (diff segments, entity-highlighted text) to
 * `maxLines` and shows Spark ghost "Show more" / "Show less" only when the
 * content overflows — same behavior as Spark `ExpandableText`, which only
 * accepts plain strings.
 */
const ClampedContent = ({ children, maxLines = 3, className = '' }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef(null);

  const measure = useCallback(() => {
    if (expanded) return;
    const el = bodyRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  useLayoutEffect(() => {
    measure();
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(measure);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [children, maxLines, measure]);

  return (
    <div className={['vdm-clamped', className].filter(Boolean).join(' ')}>
      <div
        ref={bodyRef}
        className={expanded ? 'vdm-clamped-body' : 'vdm-clamped-body vdm-clamped-body--clamped'}
        style={{ '--vdm-clamp-lines': String(maxLines) }}
      >
        {children}
      </div>
      {overflows || expanded ? (
        <Button
          type="button"
          variant="ghost"
          size="tiny"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      ) : null}
    </div>
  );
};

export default ClampedContent;
